// Firestore Service for SwipeSage Admin Panel
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  addDoc,
  updateDoc,
  deleteDoc,
  runTransaction,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  writeBatch,
  onSnapshot,
  Unsubscribe,
  type UpdateData
} from 'firebase/firestore';
import { getFirebaseFirestore } from './firebase';
import { growthWindows, nonAdminProfileCounts, type GrowthMonth } from './growth';
import { completedIstDays, summarizeActivity, type ActivitySummary } from './activity';
import { 
  User, 
  CreditCard, 
  CardApplication, 
  AnalyticsEvent, 
  COLLECTIONS 
} from '@shared/firebase/schema';

class FirestoreService {
  private db = getFirebaseFirestore();

  // Generic GET with exponential-backoff retry on offline errors.
  private async getDocument<T>(collectionName: string, docId: string): Promise<T | null> {
    const maxRetries = 3
    let lastError: any

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const docRef  = doc(this.db, collectionName, docId)
        const docSnap = await getDoc(docRef)
        if (docSnap.exists()) return { id: docSnap.id, ...docSnap.data() } as T
        return null
      } catch (error: any) {
        lastError = error
        if (attempt < maxRetries && this.isOfflineError(error)) {
          const delay = Math.pow(2, attempt - 1) * 1000 // exp backoff
          await new Promise(resolve => setTimeout(resolve, delay))
          continue
        }
        break
      }
    }

    console.error(`getDocument failed: ${collectionName}/${docId}`, lastError)
    throw lastError
  }

  // Helper to check if error is due to offline/connection issues
  private isOfflineError(error: any): boolean {
    const offlineMessages = [
      'failed to get document because the client is offline',
      'could not be completed',
      'connection failed',
      'network request failed',
      'unavailable'
    ];
    
    const errorMessage = (error?.message || '').toLowerCase();
    const errorCode = error?.code || '';
    
    return offlineMessages.some(msg => errorMessage.includes(msg)) || 
           errorCode === 'unavailable' || 
           errorCode === 'network-request-failed';
  }

  private async getDocuments<T>(
    collectionName: string,
    constraints: Array<any> = [],
    pageSize: number = 20,
    lastDoc?: QueryDocumentSnapshot<DocumentData>
  ): Promise<{ data: T[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
    try {
      let q = query(collection(this.db, collectionName), ...constraints);
      
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      q = query(q, limit(pageSize));
      const querySnapshot = await getDocs(q);
      
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as T[];
      
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
      
      return { data, lastDoc: lastVisible };
    } catch (error) {
      console.error(`Error getting documents from ${collectionName}:`, error);
      throw error;
    }
  }

  private async addDocument<T>(collectionName: string, data: Omit<T, 'id'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(this.db, collectionName), {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error(`Error adding document to ${collectionName}:`, error);
      throw error;
    }
  }

  private async updateDocument<T>(collectionName: string, docId: string, data: UpdateData<T>): Promise<void> {
    try {
      const docRef = doc(this.db, collectionName, docId);
      await updateDoc(docRef, {
        // UpdateData<T> is a conditional type TS won't spread directly;
        // its runtime shape is a plain field map, so treat it as one to
        // merge in the server-side updatedAt stamp.
        ...(data as unknown as Record<string, unknown>),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error(`Error updating document in ${collectionName}:`, error);
      throw error;
    }
  }

  private async deleteDocument(collectionName: string, docId: string): Promise<void> {
    try {
      const docRef = doc(this.db, collectionName, docId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting document from ${collectionName}:`, error);
      throw error;
    }
  }

  // Credit Cards CRUD
  async getCreditCard(cardId: string): Promise<CreditCard | null> {
    return this.getDocument<CreditCard>(COLLECTIONS.CREDIT_CARDS, cardId);
  }

  /**
   * Get credit cards with optional filtering and sorting
   * 
   * INDEXING STRATEGY:
   * - When no filters: Uses single-field index on 'createdAt' for fast sorting
   * - When filters applied: Avoids composite index requirement by sorting in JavaScript
   * - Composite indexes deployed for optimal performance when ready:
   *   * bank + createdAt
   *   * category + createdAt  
   *   * isActive + createdAt
   *   * isFeatured + createdAt
   * 
   * This hybrid approach ensures immediate functionality while indexes build in background.
   */
  async getCreditCards(
    filters: {
      category?: string;
      bank?: string;
      isActive?: boolean;
      isFeatured?: boolean;
    } = {},
    pageSize: number = 20,
    lastDoc?: QueryDocumentSnapshot<DocumentData>
  ): Promise<{ data: CreditCard[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
    const constraints: Array<any> = [];
    
    // Check if we have any filters that would require composite indexes
    const hasFilters = filters.category || filters.bank || 
                      filters.isActive !== undefined || filters.isFeatured !== undefined;
    
    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }
    if (filters.bank) {
      constraints.push(where('bank', '==', filters.bank));
    }
    if (filters.isActive !== undefined) {
      constraints.push(where('isActive', '==', filters.isActive));
    }
    if (filters.isFeatured !== undefined) {
      constraints.push(where('isFeatured', '==', filters.isFeatured));
    }
    
    // Only add orderBy if no filters are applied to avoid composite index requirement
    // When filters are applied, we'll sort in JavaScript after fetching
    if (!hasFilters) {
      constraints.push(orderBy('createdAt', 'desc'));
    }
    
    const result = await this.getDocuments<CreditCard>(COLLECTIONS.CREDIT_CARDS, constraints, pageSize, lastDoc);
    
    // If we have filters, sort the results in JavaScript
    if (hasFilters && result.data.length > 0) {
      result.data.sort((a, b) => {
        const aDate = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const bDate = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return bDate.getTime() - aDate.getTime();
      });
    }
    
    return result;
  }

  async addCreditCard(cardData: Omit<CreditCard, 'id'>): Promise<string> {
    return this.addDocument<CreditCard>(COLLECTIONS.CREDIT_CARDS, cardData);
  }

  async updateCreditCard(cardId: string, cardData: UpdateData<CreditCard>): Promise<void> {
    return this.updateDocument<CreditCard>(COLLECTIONS.CREDIT_CARDS, cardId, cardData);
  }

  async deleteCreditCard(cardId: string): Promise<void> {
    return this.deleteDocument(COLLECTIONS.CREDIT_CARDS, cardId);
  }

  // Users CRUD
  async getUser(userId: string): Promise<User | null> {
    return this.getDocument<User>(COLLECTIONS.USERS, userId);
  }

  // Search and sorting need a complete profile set; Firestore cannot run the
  // page's substring search. This bounded scan fails explicitly at the cap
  // instead of showing a partial directory as if it were all users.
  async getUserDirectory(): Promise<User[]> {
    const MAX_PROFILES = 500;
    const snapshot = await getDocs(query(collection(this.db, COLLECTIONS.USERS), limit(MAX_PROFILES + 1)));
    if (snapshot.size > MAX_PROFILES) {
      throw new Error(`User directory exceeds ${MAX_PROFILES} profiles. A server-side search is required.`);
    }
    return snapshot.docs.map(item => ({
      ...item.data(),
      uid: item.id,
    })) as User[];
  }

  async createUser(userId: string, userData: User): Promise<void> {
    try {
      const userRef = doc(this.db, COLLECTIONS.USERS, userId);
      // Admin auth bootstrap can run on every page reload and twice during
      // sign-in. A transaction makes this create-only even across two tabs.
      await runTransaction(this.db, async transaction => {
        const existing = await transaction.get(userRef);
        if (!existing.exists()) transaction.set(userRef, userData);
      });
    } catch (error) {
      console.error(`Error creating user ${userId}:`, error);
      throw error;
    }
  }

  async getDashboardGrowth(now = new Date()): Promise<GrowthMonth[]> {
    const applications = collection(this.db, COLLECTIONS.CARD_APPLICATIONS);
    const windows = growthWindows(now);
    const [profiles, applicationCounts] = await Promise.all([
      this.getUserDirectory(),
      Promise.all(windows.map(async window => {
        const result = await getCountFromServer(query(applications,
          where('appliedAt', '>=', window.start),
          where('appliedAt', '<', window.end)));
        return result.data().count;
      })),
    ]);
    const profileCounts = nonAdminProfileCounts(profiles, windows);
    return windows.map((window, index) => ({
        key: window.key,
        label: window.label,
        partial: window.partial,
        profiles: profileCounts[index],
        applications: applicationCounts[index],
    }));
  }

  async getRecentActivity(now = new Date()): Promise<ActivitySummary> {
    const MAX_EVENTS = 1000;
    const window = completedIstDays(now);
    const events = collection(this.db, COLLECTIONS.ANALYTICS_EVENTS);
    const snapshot = await getDocs(query(events,
      where('timestamp', '>=', window.start),
      where('timestamp', '<', window.end),
      orderBy('timestamp', 'desc'),
      limit(MAX_EVENTS + 1)));
    if (snapshot.size > MAX_EVENTS) {
      throw new Error(`Activity exceeds ${MAX_EVENTS} events in this window; chart would be incomplete.`);
    }
    return summarizeActivity(snapshot.docs.map(item => item.data()), now);
  }

  async updateUser(userId: string, userData: Partial<User>): Promise<void> {
    return this.updateDocument<User>(COLLECTIONS.USERS, userId, userData);
  }

  // Card Applications CRUD
  async getCardApplication(applicationId: string): Promise<CardApplication | null> {
    return this.getDocument<CardApplication>(COLLECTIONS.CARD_APPLICATIONS, applicationId);
  }

  async getCardApplications(
    filters: {
      userId?: string;
      cardId?: string;
      status?: string;
    } = {},
    pageSize: number = 20,
    lastDoc?: QueryDocumentSnapshot<DocumentData>
  ): Promise<{ data: CardApplication[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
    const constraints: Array<any> = [];
    
    if (filters.userId) {
      constraints.push(where('userId', '==', filters.userId));
    }
    if (filters.cardId) {
      constraints.push(where('cardId', '==', filters.cardId));
    }
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    
    constraints.push(orderBy('appliedAt', 'desc'));
    
    return this.getDocuments<CardApplication>(COLLECTIONS.CARD_APPLICATIONS, constraints, pageSize, lastDoc);
  }

  async updateCardApplication(applicationId: string, applicationData: Partial<CardApplication>): Promise<void> {
    return this.updateDocument<CardApplication>(COLLECTIONS.CARD_APPLICATIONS, applicationId, applicationData);
  }

  // Analytics
  async getAnalyticsEvents(
    filters: {
      event?: string;
      userId?: string;
      platform?: string;
      startDate?: Date;
      endDate?: Date;
    } = {},
    pageSize: number = 100,
    lastDoc?: QueryDocumentSnapshot<DocumentData>
  ): Promise<{ data: AnalyticsEvent[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
    const constraints: Array<any> = [];
    
    if (filters.event) {
      constraints.push(where('event', '==', filters.event));
    }
    if (filters.userId) {
      constraints.push(where('userId', '==', filters.userId));
    }
    if (filters.platform) {
      constraints.push(where('platform', '==', filters.platform));
    }
    if (filters.startDate) {
      constraints.push(where('timestamp', '>=', filters.startDate));
    }
    if (filters.endDate) {
      constraints.push(where('timestamp', '<=', filters.endDate));
    }
    
    constraints.push(orderBy('timestamp', 'desc'));
    
    return this.getDocuments<AnalyticsEvent>(COLLECTIONS.ANALYTICS_EVENTS, constraints, pageSize, lastDoc);
  }

  // Real-time listeners
  onCreditCardsChange(callback: (cards: CreditCard[]) => void): Unsubscribe {
    const q = query(collection(this.db, COLLECTIONS.CREDIT_CARDS), orderBy('createdAt', 'desc'));
    
    return onSnapshot(q, (querySnapshot) => {
      const cards = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as CreditCard[];
      callback(cards);
    });
  }

  onCardApplicationsChange(callback: (applications: CardApplication[]) => void): Unsubscribe {
    const q = query(collection(this.db, COLLECTIONS.CARD_APPLICATIONS), orderBy('appliedAt', 'desc'));
    
    return onSnapshot(q, (querySnapshot) => {
      const applications = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as CardApplication[];
      callback(applications);
    });
  }

  // Batch operations
  async batchUpdateCreditCards(updates: Array<{ id: string; data: Partial<CreditCard> }>): Promise<void> {
    const batch = writeBatch(this.db);
    
    updates.forEach(({ id, data }) => {
      const docRef = doc(this.db, COLLECTIONS.CREDIT_CARDS, id);
      batch.update(docRef, {
        ...data,
        updatedAt: new Date()
      });
    });
    
    await batch.commit();
  }

  // Statistics
  async getDashboardStats(): Promise<{
    totalUsers: number;
    totalCards: number;
    totalApplications: number;
    pendingApplications: number;
    approvedApplications: number;
    rejectedApplications: number;
  }> {
    try {
      // Firestore count() aggregations: billed as 1 read per 1000 docs
      // counted, instead of 1 read per doc. At 10k applications that's
      // 10 reads vs 10000 — orders of magnitude cheaper at scale.
      const apps = collection(this.db, COLLECTIONS.CARD_APPLICATIONS);
      const [users, cards, total, pending, approved, rejected] = await Promise.all([
        getCountFromServer(collection(this.db, COLLECTIONS.USERS)),
        getCountFromServer(collection(this.db, COLLECTIONS.CREDIT_CARDS)),
        getCountFromServer(apps),
        getCountFromServer(query(apps, where('status', '==', 'pending'))),
        getCountFromServer(query(apps, where('status', '==', 'approved'))),
        getCountFromServer(query(apps, where('status', '==', 'rejected'))),
      ]);

      return {
        totalUsers:           users.data().count,
        totalCards:           cards.data().count,
        totalApplications:    total.data().count,
        pendingApplications:  pending.data().count,
        approvedApplications: approved.data().count,
        rejectedApplications: rejected.data().count,
      };
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      throw error;
    }
  }
}

export const firestoreService = new FirestoreService();
export default firestoreService;
